import React, { useState, useRef, useEffect } from "react";
import { Search } from "lucide-react";
import "./DropDown.css"; // Keep your existing CSS file for additional styles

const DropDown2 = ({
  options = [],
  onChange,
  placeholder = "Select an option",
  className = "",
  icon,
  search = false,
  label,
  name,
  required = false,
  defaultValue = "",
  labelClass = "font-bold text-bases",
}) => {
  // Find the default option based on defaultValue prop
  const defaultOption = options.find((option) => option.value === defaultValue);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(defaultOption || null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update selected option when defaultValue changes
  useEffect(() => {
    const option = options.find((option) => option.value === defaultValue);
    if (option) {
      setSelectedOption(option);
    }
  }, [defaultValue, options]);

  // Filter options based on search term
  const filteredOptions = search
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && search && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, search]);

  // Handle option selection
  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    setSearchTerm("");

    if (onChange) {
      onChange(option.value); // Pass only the value
    }
  };

  // Toggle dropdown open/closed
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && search) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <label className={labelClass} htmlFor={name}>
          {label}
          {required && <span className="text-red-500 text-xs ml-0.5">*</span>}
        </label>
      )}

      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer transition-all ${
            isOpen ? "ring-2 ring-blue-200 border-blue-300" : ""
          }`}
          onClick={toggleDropdown}
        >
          <div className="flex-1 flex items-center">
            {icon && <div className="mr-2">{icon}</div>}

            {isOpen && search ? (
              <div className="flex items-center w-full">
                <Search size={16} className="text-gray-400 mr-2" />
                <input
                  type="text"
                  className="w-full outline-none bg-transparent"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  ref={searchInputRef}
                />
              </div>
            ) : (
              <span className={`${!selectedOption && "text-gray-500"}`}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                    selectedOption?.value === option.value
                      ? "bg-blue-50 text-blue-600"
                      : ""
                  }`}
                  onClick={() => handleSelectOption(option)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 italic">
                No options found
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default DropDown2;
